/**
 * The settings PAGES the registry may point at — DERIVED from the existing
 * destination authorities, never restated (#2328 §6, skill rule 42).
 *
 * #2328 §6 forbids *"a second central list of settings pages in the Studio"*,
 * and §9 says to *"register existing settings destinations without cloning
 * their forms or storage"*. A hand-typed array of the seven platform pages plus
 * one entry per installed module would be exactly the second list — and it is
 * not hypothetical: `settingsNavigation.ts:86-97` records that the FIRST fix
 * for the duplicate-settings-navigation defect was itself a hand-maintained
 * restatement, where two lists *"agreed only as long as somebody remembered
 * they had to"*.
 *
 * ── TWO AUTHORITIES, COMPOSED HERE AND NOWHERE ELSE ────────────────────────
 * `settingsDestinations(modules, demo)` — the `/settings?tab=` pages: this
 * deployment's own, plus one per installed module that declares a setting, plus
 * Sandbox on the demo transport. The same function the console shell's Settings
 * group, the settings page's own navigator and its compact selector read.
 *
 * `studioWorkspaceDestinations(fleet)` — the thirteen administration workspaces
 * ROOT ruling 74071 §6 moved out of the Platform nav group and into registered
 * Settings Studio categories. The fourteenth, the Approval Inbox, is classified
 * `business` and therefore never becomes a page here; that classification is
 * the reason it is absent, and `platformWorkspaces.ts` carries it.
 *
 * `definitions.parity.test.ts` asserts the identity in both directions and at
 * several deployment shapes: the registry can neither carry a page neither
 * authority declares, nor miss one either does.
 *
 * ── WHAT A PAGE ADDS OVER A DESTINATION ────────────────────────────────────
 * A destination is `{ key, label, to }`. A registered page splits `to` into the
 * ROUTE and the TAB, records which authority it came from, and adds the page's
 * declared control ANCHORS — the three things #2328 §6 requires registration to
 * be able to REFUSE:
 *
 *   unknown route   -> a definition whose `route` is not this page's route
 *   unknown tab     -> a definition whose `tab` is not this page's tab
 *   unknown anchor  -> a `controlAnchor` this page never declared
 *
 * The route and tab are parsed from the destination rather than typed beside
 * it, for the same reason the page set is: a destination that moved to a new
 * route moves here with it, and cannot be pointed at its old one.
 *
 * SCAN INPUT: `settingsDestinations()` from
 * `apps/console/src/app/routes/settingsNavigation.ts`, plus the installed
 * modules that declare a setting (`useSettingModules()` -> `core.setting_def`
 * module names, never their values) and the demo-transport flag; and
 * `PLATFORM_WORKSPACES` from `platformWorkspaces.ts`, gated by the same `fleet`
 * predicate `builtinNav` gates them with.
 */
import { settingsDestinations, type SettingsDestination } from '../app/routes/settingsNavigation'
import { studioWorkspaceDestinations } from './platformWorkspaces'
import type { SettingsPage, SettingsPageOrigin } from './SettingDefinition'

/**
 * The control anchors each settings page declares (#2328 §6, §7 step 3).
 *
 * EMPTY, and measured rather than assumed: no settings panel on this base
 * renders a stable section `id` for a palette result to scroll to. Registering
 * an anchor that nothing renders would give the settings palette a result that
 * navigates and then focuses nothing — #2328 §3's *"do not render placeholder/
 * dead tiles"* one surface over, and §8's *"a failed registry is distinct from
 * no actions available"* from the other side.
 *
 * Every entry here is therefore owed a rendered anchor in the same change. In
 * particular ruling 74071 §4's settings-search journey — *"use
 * `Regional & currency > Currency rates`"* — needs `regional: ['currency-rates']`
 * here AND a rendered `id="currency-rates"` on that panel, which is the Studio's
 * change (B4) and not a registration. Until the pair lands together, every
 * `controlAnchor` is refused at registration, which is the honest state and is
 * proven both ways in `registry.test.ts`.
 */
export const SETTINGS_CONTROL_ANCHORS: Readonly<Record<string, readonly string[]>> = {
  /* Ruling 74071 §4's settings-search journey, and the ONE anchor this packet
     registers: *"use `Regional & currency > Currency rates`. Do NOT invent an
     SMTP page to satisfy a test sentence."*

     The pair landed together, which is the condition this comment used to state
     as an obligation: `CurrencyRates.tsx` renders `id="currency-rates"` on the
     section it draws, and `settingsStudio.anchor.test.ts` asserts that the id
     is in the source of the panel this anchor names — so a registration can
     never outlive the thing it scrolls to. Delete the id and the registration
     goes red; delete the registration and the Studio's own parity test does.

     Every OTHER page still declares none, and that is a measurement rather than
     an omission: no other settings panel on this base renders a stable section
     id. An anchor nothing renders is a deep link that navigates and then focuses
     nothing, so every other `controlAnchor` stays refused at registration. */
  regional: ['currency-rates'],
}

/**
 * The route the Settings Studio itself renders (#2328 §5, ruling 74071 §3).
 *
 * Named once because THREE different questions ask it: which pages the Studio
 * draws inside its overlay, which addresses the legacy `?tab=` redirect
 * rewrites, and which destinations the studio rail links at their own route
 * instead. Typing `/settings` at each of them is how the three drift.
 */
export const SETTINGS_ROUTE = '/settings'

/**
 * Is this pathname inside the Settings Studio?
 *
 * It lives HERE, beside the route constant it reads, rather than in
 * `SettingsStudio.tsx` where it started. The reason is a build warning this
 * packet produced and then read: `SettingsStudio` is a LAZY route
 * (`router.tsx`), and `AppShell` plus `app/palette/owner.ts` both need this
 * predicate — so importing it from the component statically pulled the whole
 * Studio into the shell's chunk and Vite said so outright (*"dynamically
 * imported … but also statically imported … dynamic import will not move module
 * into another chunk"*). A route predicate is a leaf fact about an address; it
 * has no business dragging a workspace behind it.
 *
 * `startsWith` on the route PLUS a separator, never on the route alone:
 * `/settingsomething` is not the Studio, and a shell made inert for it would be
 * a page nobody could operate with no error anywhere.
 */
export function isStudioPath(pathname: string): boolean {
  return pathname === SETTINGS_ROUTE || pathname.startsWith(`${SETTINGS_ROUTE}/`)
}

/**
 * Does the Studio RENDER this page, or merely index it?
 *
 * Derived from the page's own route, which is the whole reason it is safe: a
 * page whose authoritative route IS the settings route has its panel drawn
 * inside the Studio overlay; a page routed anywhere else — the thirteen
 * administration workspaces ruling 74071 §6 moved in — is a full console
 * surface with its own subroutes, search params and record state, and the
 * Studio points at it rather than cloning it.
 *
 * #2328 §6 is explicit that this is what the Studio is: *"an index and shell
 * around existing authoritative setting doors, not a new generic key/value
 * editor"*. Rendering `/flows` inside a near-full-viewport overlay that owns
 * Escape and marks the shell inert would give one surface two competing route
 * states — and re-implementing it there would be the clone §9 forbids.
 *
 * No judgement, no second list: the answer is a property of the destination.
 */
export function rendersInStudio(page: SettingsPage): boolean {
  return page.route === SETTINGS_ROUTE
}

/**
 * The Studio's own routed address for a page it renders (ruling 74071 §3:
 * *"`/settings/{category}/{page}[?tab=...][#control-id]`"*).
 *
 * Raises on a page the Studio does not render, rather than minting an address
 * for it: a `/settings/environments/modules` that resolved to nothing would be
 * a deep link somebody could paste, bookmark, or find in a palette.
 */
export function studioPath(category: string, pageId: string): string {
  return `${SETTINGS_ROUTE}/${encodeURIComponent(category)}/${encodeURIComponent(pageId)}`
}

/**
 * Where a registered destination is REACHED, given the category it registered
 * under — the one address the Studio rail, the settings palette and a deep link
 * all use.
 *
 *   a page the Studio renders   -> `/settings/{category}/{page}`
 *   a page it only indexes      -> the destination's own route, unchanged
 *
 * The second case is what makes ruling §6 true rather than merely stated: a
 * workspace moved into the Studio is reached at ITS route and at no second
 * address, so "moved" means the entry moved, not that the surface was copied.
 */
export function studioHref(page: SettingsPage, category: string): string {
  return rendersInStudio(page) ? studioPath(category, page.id) : pageHref(page)
}

/**
 * The prefix `settingsDestinations()` keys an installed module's destination
 * with (`settingsNavigation.ts:33`), and which `settingsNavGroups` already
 * partitions on (`:64-65`). Named once here so the registry's definition files
 * split the page set exactly where the existing navigator does, rather than
 * each carrying its own idea of what a module page looks like.
 */
export const MODULE_PAGE_PREFIX = 'module-'

/** `/settings?tab=branding` -> `{ route: '/settings', tab: 'branding' }`. */
function addressOf(destination: SettingsDestination): { route: string; tab?: string } {
  const [route, search] = destination.to.split('?', 2)
  if (route === undefined || route === '' || !route.startsWith('/')) {
    throw new Error(
      `settingsPages: destination "${destination.key}" has no route in "${destination.to}"`,
    )
  }
  if (search === undefined || search === '') return { route }
  const tab = new URLSearchParams(search).get('tab')
  /* A destination that carries a search string this cannot read is a LOUD
     failure, not a page with no tab: silently dropping it would register a page
     addressing the route's default tab while claiming to address another. */
  if (tab === null || tab === '') {
    throw new Error(
      `settingsPages: destination "${destination.key}" carries "${search}", `
      + 'which declares no tab — teach pages.ts its shape',
    )
  }
  return { route, tab }
}

/**
 * Every settings page this deployment actually has, in destination order.
 *
 * Raises on an empty derivation and on a duplicate page id (skill rule 42): a
 * page set that came back empty would make every definition unregisterable AND
 * every refusal test pass for the wrong reason, and two pages sharing an id
 * would give one destination two categories with nothing to say which won.
 */
export function settingsPages(
  modules: readonly string[], demo = false, fleet = false,
): SettingsPage[] {
  const sources: { destination: SettingsDestination; origin: SettingsPageOrigin }[] = [
    ...settingsDestinations(modules, demo).map((destination) => ({
      destination,
      origin: (destination.key.startsWith(MODULE_PAGE_PREFIX) ? 'module' : 'deployment') as SettingsPageOrigin,
    })),
    ...studioWorkspaceDestinations(fleet).map((destination) => ({
      destination,
      origin: 'workspace' as SettingsPageOrigin,
    })),
  ]
  if (sources.length === 0) {
    throw new Error('settingsPages: the destination authorities yielded nothing — there is no settings authority to index')
  }
  const pages = sources.map(({ destination, origin }) => {
    const { route, tab } = addressOf(destination)
    return {
      id: destination.key,
      origin,
      route,
      ...(tab === undefined ? {} : { tab }),
      label: destination.label,
      anchors: SETTINGS_CONTROL_ANCHORS[destination.key] ?? [],
    }
  })
  const seen = new Set<string>()
  for (const page of pages) {
    if (seen.has(page.id)) {
      throw new Error(
        `settingsPages: two destinations claim the page id "${page.id}" — `
        + 'one destination, one address',
      )
    }
    seen.add(page.id)
  }
  return pages
}

/** The deep link for a page's own address — the same string the destination carries. */
export function pageHref(page: SettingsPage): string {
  return page.tab === undefined ? page.route : `${page.route}?tab=${encodeURIComponent(page.tab)}`
}

/** Is this page one installed module's declared settings? */
export function isModuleSettingsPage(page: SettingsPage): boolean {
  return page.origin === 'module'
}

/** Is this page one of the administration workspaces ruling 74071 §6 moved in? */
export function isWorkspaceSettingsPage(page: SettingsPage): boolean {
  return page.origin === 'workspace'
}

/** Is this page one of the deployment's own `/settings?tab=` destinations? */
export function isDeploymentSettingsPage(page: SettingsPage): boolean {
  return page.origin === 'deployment'
}

/**
 * The module a module page belongs to.
 *
 * Read off the page's TAB, which is the module name the URL actually carries
 * (`?tab=crm`), not off the id's prefix — the `mod:` form in `Settings.tsx:60-63`
 * is an internal encoding that deliberately never appears in a link.
 */
export function moduleOfSettingsPage(page: SettingsPage): string {
  if (!isModuleSettingsPage(page) || page.tab === undefined || page.tab === '') {
    throw new Error(`moduleOfSettingsPage: "${page.id}" is not an installed module's settings page`)
  }
  return page.tab
}
